import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Paper,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { eventFailureTicketsStyles } from './styles';

const SUMMARY_FIELDS = [
  ['total_flows_fetched', 'Flows Fetched', 'Total number of test flows retrieved from the source'],
  [
    'new_flows_processed',
    'New Flows',
    'Number of flows encountered for the first time and processed in this run',
  ],
  [
    'already_processed_flows',
    'Already Processed',
    'Number of flows skipped because they were processed in a previous run',
  ],
  [
    'failed_flow_fetches',
    'Fetch Failures',
    'Number of flows that could not be retrieved due to errors',
  ],
  [
    'total_failures_detected',
    'Failures Detected',
    'Total number of failures found across all processed flows',
  ],
  [
    'unique_beacons',
    'Unique Beacons',
    'Number of distinct beacon signals identified from the detected failures',
  ],
  [
    'tickets_created',
    'Tickets Created',
    'Number of new Jira tickets created for detected failures',
  ],
  [
    'tickets_commented',
    'Tickets Commented',
    'Number of existing Jira tickets updated with new comments',
  ],
  [
    'tickets_skipped',
    'Tickets Skipped',
    'Number of tickets not acted on, including beacons that failed after retries or were already up-to-date/below threshold.',
  ],
];

function getActionColor(action) {
  if (action === 'created') return 'success';
  if (action === 'commented') return 'info';
  if (action === 'skipped') return 'warning';
  return 'default';
}

export default function EventFailureTicketsDialog({
  open,
  onClose,
  payload,
  searchFilters = {},
  jobId,
  status,
  polling,
  starting,
  response,
  error,
}) {
  const results = Array.isArray(response?.results) ? response.results : [];
  const hasTicketActivity =
    Number(response?.tickets_created || 0) > 0 || Number(response?.tickets_commented || 0) > 0;
  const showProgress = (starting || polling || jobId) && !response && !error;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={eventFailureTicketsStyles.dialogTitle}>
        Jira Event Failure Tickets
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={eventFailureTicketsStyles.content}>
          <Paper variant="outlined" sx={eventFailureTicketsStyles.contextPanel}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Request
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Regression
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {payload?.regression_test_id || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Market
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {searchFilters?.market_name || payload?.market_id || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Device
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {searchFilters?.device_name || payload?.device_id || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {showProgress && (
            <Alert
              severity="info"
              icon={<CircularProgress size={18} color="inherit" />}
              sx={eventFailureTicketsStyles.progressAlert}
            >
              <Typography fontWeight={700}>Ticket job is running</Typography>
              <Typography variant="body2">
                {jobId ? `Job is ${status || 'running'}.` : 'Submitting ticket job...'}
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error">
              <Typography fontWeight={700}>Ticket creation failed</Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {response && (
            <>
              <Alert severity={hasTicketActivity ? 'success' : 'info'}>
                {hasTicketActivity
                  ? 'Event failure ticket processing completed.'
                  : 'Event failure ticket processing completed. No ticket actions were needed.'}
              </Alert>

              <Grid container spacing={1.5}>
                {SUMMARY_FIELDS.map(([key, label, tooltip]) => (
                  <Grid item xs={6} md={4} key={key}>
                    <Tooltip title={tooltip} arrow placement="top">
                      <Paper variant="outlined" sx={eventFailureTicketsStyles.summaryTile}>
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {response?.[key] ?? 0}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Results
                </Typography>

                {results.length > 0 ? (
                  <Box sx={eventFailureTicketsStyles.resultsList}>
                    {results.map((item, index) => (
                      <Paper
                        key={`${item?.ticket_key || item?.beacon_id || index}`}
                        variant="outlined"
                        sx={eventFailureTicketsStyles.resultItem}
                      >
                        <Box sx={eventFailureTicketsStyles.resultHeader}>
                          <Box sx={eventFailureTicketsStyles.resultIdentity}>
                            <Typography variant="body2" fontWeight={700}>
                              Beacon {item?.beacon_id || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item?.event_name || 'Unknown event'}
                            </Typography>
                          </Box>

                          <Box sx={eventFailureTicketsStyles.resultMeta}>
                            <Chip
                              label={item?.action || 'unknown'}
                              color={getActionColor(item?.action)}
                              size="small"
                              sx={eventFailureTicketsStyles.actionChip}
                            />
                            {item?.ticket_url && item?.ticket_key ? (
                              <Link
                                href={item.ticket_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={eventFailureTicketsStyles.ticketLink}
                              >
                                {item.ticket_key}
                              </Link>
                            ) : (
                              <Typography variant="caption" sx={eventFailureTicketsStyles.noTicket}>
                                No ticket
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {item?.message && (
                          <Typography variant="body2" sx={eventFailureTicketsStyles.resultMessage}>
                            {item.message}
                          </Typography>
                        )}

                        {item?.error_reason && (
                          <Typography variant="caption" sx={eventFailureTicketsStyles.errorReason}>
                            {item.error_reason}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info">No result entries returned by the ticket service.</Alert>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
